import { Link } from '@tanstack/react-router'
import { CtaButton, ctaLinkClasses } from '@workspace/ui/components/dsm/cta-button'
import { useState } from 'react'
import {
  useClaimFounderInviteMutation,
  useFounderInvitePreviewQuery,
  useMyMembershipsQuery,
} from '../../../hooks/api'
import { useSession } from '../../../lib/auth'
import { FOUNDER_COPY, fill } from '../copy/founder-invite-copy'
import { type InvestorView, verifiedIdentity } from '../invite-landing'

/**
 * The page a founder lands on from an investor's invite link. Public: it works signed out. Identity is
 * shown only when the investor's account check has passed; every investor-typed string is plain text.
 */
export function FounderInvitePage({ token }: { token: string }) {
  const preview = useFounderInvitePreviewQuery(token)
  return (
    <>
      {/* React 19 hoists these into the document head. */}
      <title>{FOUNDER_COPY.title}</title>
      <meta name="robots" content="noindex" />
      <meta name="referrer" content="no-referrer" />
      <main className="mx-auto max-w-xl space-y-6 px-4 py-16">
        {preview.data === 'not-found' && (
          <p className="text-base text-foreground">{FOUNDER_COPY.invalid}</p>
        )}
        {preview.isError && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground" role="alert">
              {FOUNDER_COPY.loadError}
            </p>
            <CtaButton type="button" onClick={() => preview.refetch()}>
              {FOUNDER_COPY.retry}
            </CtaButton>
          </div>
        )}
        {preview.data && preview.data !== 'not-found' && (
          <InviteContent
            startupName={preview.data.startupName}
            investor={preview.data.investor}
            token={token}
          />
        )}
      </main>
    </>
  )
}

function InviteContent({
  startupName,
  investor,
  token,
}: {
  startupName: string
  investor: InvestorView
  token: string
}) {
  const identity = verifiedIdentity(investor)
  const investorName = identity?.name ?? 'the investor'
  const headline = identity
    ? fill(identity.firm ? 'headline' : 'headlineNoFirm', {
        name: identity.name,
        firm: identity.firm ?? '',
        startup: startupName,
      })
    : fill('headlineUnverified', { startup: startupName })

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{headline}</h1>
        {!identity && (
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{startupName}</span>{' '}
            <span>{FOUNDER_COPY.unverifiedLabel}</span>
          </p>
        )}
      </div>
      <p className="text-base text-foreground">{FOUNDER_COPY.intro}</p>
      <p className="text-sm text-muted-foreground">
        {identity ? fill('hearsVerified', { name: identity.name }) : FOUNDER_COPY.hearsUnverified}
      </p>
      <Actions investorName={investorName} token={token} />
    </div>
  )
}

function Actions({ investorName, token }: { investorName: string; token: string }) {
  const { data: session } = useSession()
  return (
    <div className="space-y-6">
      {session ? (
        <JoinWithStartup investorName={investorName} token={token} />
      ) : (
        <div className="flex flex-wrap items-center gap-4">
          <Link to="/app" className={ctaLinkClasses()}>
            {FOUNDER_COPY.upload}
          </Link>
          <Link
            to="/app"
            className="text-sm font-medium text-foreground underline-offset-2 hover:underline"
          >
            {FOUNDER_COPY.signIn}
          </Link>
        </div>
      )}
    </div>
  )
}

function JoinWithStartup({ investorName, token }: { investorName: string; token: string }) {
  const memberships = useMyMembershipsQuery()
  const startups = (memberships.data ?? []).filter((m) => m.type === 'startup')
  const [startupId, setStartupId] = useState('')
  const claim = useClaimFounderInviteMutation()

  if (claim.isSuccess) {
    return (
      <div className="space-y-4">
        <p className="text-base text-foreground">{fill('claimDone', { investor: investorName })}</p>
        <Link to="/app" className={ctaLinkClasses()}>
          {FOUNDER_COPY.upload}
        </Link>
      </div>
    )
  }
  if (memberships.isSuccess && startups.length === 0) {
    return <p className="text-sm text-muted-foreground">{FOUNDER_COPY.claimNoStartup}</p>
  }
  const selected = startupId || startups[0]?.id || ''
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (selected) claim.mutate({ path: { token }, body: { startupId: selected } })
      }}
    >
      <label className="block space-y-1.5">
        <span className="text-sm font-medium text-foreground">{FOUNDER_COPY.claimLabel}</span>
        <select
          value={selected}
          onChange={(event) => setStartupId(event.target.value)}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
        >
          {startups.map((startup) => (
            <option key={startup.id} value={startup.id}>
              {startup.name}
            </option>
          ))}
        </select>
      </label>
      {claim.isError && (
        <p role="alert" className="text-sm text-muted-foreground">
          {FOUNDER_COPY.claimError}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-4">
        <CtaButton type="submit" disabled={!selected || claim.isPending}>
          {FOUNDER_COPY.claimSubmit}
        </CtaButton>
        <Link
          to="/app"
          className="text-sm font-medium text-foreground underline-offset-2 hover:underline"
        >
          {FOUNDER_COPY.upload}
        </Link>
      </div>
    </form>
  )
}
